/**
 * The sheet — the app's one modal surface (DESIGN_SYSTEM §4.3).
 *
 * A bottom sheet on a phone, a centred full-bleed band on a wide screen. Never
 * a card (D-1): it spans the width, its only edges are a top radius on a phone
 * and hairlines on desktop, and it carries no shadow. The confirm, period and
 * view sheets each grew their own copy of this idiom; new sheets use this one,
 * and existing sheets share this surface.
 *
 * It is a real dialog: `aria-modal`, labelled by its title, Escape and a tap
 * on the scrim both close it (W-8), focus moves into it on open and back to
 * whatever opened it on close. The 44px Close handle and title accept pointer
 * drags: a quarter-height (72px floor) or downward flick dismisses. Entrance
 * slides for 240ms, exit for 200ms before onClose; reduced motion snaps and
 * desktop uses fades without dragging. Scrollable content never starts a drag.
 *
 * Portalled to `<body>`. A screen that layers its own water behind its content
 * is an `isolate` stacking context, and a sheet rendered inside one is capped
 * at that context's level — under the shell's tab bar, whatever its z-index.
 */

import { useHaptics } from "@/hooks/use_haptics";
import { type ReactNode, useEffect, useId, useState, useRef } from "react";
import { createPortal } from "react-dom";

import "./sheet.css";
import { useSheetDrag } from "@/hooks/use_sheet_drag";
import { WIDE_QUERY, useWide } from "@/hooks/use_wide";
import { cx } from "@/features/common/primitives";

export function Sheet({
  title,
  onClose,
  children,
  footer,
  closeOnEscape = true,
  desktopPanel = false,
  openHaptic = true,
  label,
  blockKeys = false,
  escapeDisabled = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode | ((close: () => void) => ReactNode);
  label?: string;
  blockKeys?: boolean;
  escapeDisabled?: boolean;
  /** Pinned under the content — the sheet's actions. */
  footer?: ReactNode | ((close: () => void) => ReactNode);
  /** False for a sheet that must not close on Escape at all. */
  closeOnEscape?: boolean;
  /** Edit stays alongside the Ledger on a wide screen. */
  desktopPanel?: boolean;
  openHaptic?: boolean;
}): React.JSX.Element {
  const titleId = useId();
  const wide = useWide();
  const haptic = useHaptics();
  const opened = useRef(false);
  useEffect(() => { if (!opened.current) { opened.current = true; if (openHaptic && (typeof window.matchMedia !== "function" || !window.matchMedia(WIDE_QUERY).matches)) haptic("tap"); } }, [openHaptic, haptic]);
  const { dialog, scrim, close, headerProps, onClickCapture } = useSheetDrag(onClose);

  // The opener is read during the first render, not in the effect: React
  // applies a child's `autoFocus` during commit, before effects run, so by then
  // the active element is already the sheet's own field. A sheet whose field
  // took focus keeps it — that is what raises the keyboard on a phone.
  const [opener] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));
  useEffect(() => {
    const node = dialog.current;
    if (node !== null && !node.contains(document.activeElement)) node.focus();
    return () => opener?.focus();
  }, [dialog, opener]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] !== dialog.current) return;
      if (blockKeys) event.stopImmediatePropagation();
      if (event.key === "Escape" && closeOnEscape && !escapeDisabled) {
        event.stopImmediatePropagation();
        close();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [blockKeys, closeOnEscape, escapeDisabled, close, dialog]);

  return createPortal(
    <div
      role="presentation"
      onClick={close}
      onClickCapture={onClickCapture}
      // A sheet covers the tab bar, so nothing in it rises above one. The shell
      // mirrors the bar's height onto the root for floating toasts; zero it here.
      style={{ "--shell-tabs": "0px" } as React.CSSProperties}
      className={cx("fixed inset-0 z-50 flex flex-col justify-end md:justify-center",
        desktopPanel && "ledger-editor-panel")}
    >
      <div ref={scrim} aria-hidden="true" className="sheet-scrim absolute inset-0 bg-black/40" />
      <div
        ref={dialog}
        role="dialog"
        aria-modal={desktopPanel && wide ? undefined : true}
        aria-label={label}
        aria-labelledby={label === undefined ? titleId : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cx(
          "sheet-dialog relative flex max-h-touch flex-col bg-bg pt-phi-n2 outline-none md:pt-phi-1",
          "rounded-t-phi-0 md:rounded-none md:border-y md:border-line",
          desktopPanel && "[@media(min-width:900px)]:h-full [@media(min-width:900px)]:max-h-full [@media(min-width:900px)]:border-l",
        )}
      >
        <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
          <header {...headerProps} className="sheet-header shrink-0">
            <button data-no-press type="button" aria-label="Close" onClick={close} className="mx-auto flex min-h-[44px] min-w-[44px] items-center justify-center md:hidden">
              <span aria-hidden="true" className="h-1 w-8 rounded-full bg-line" />
            </button>
            <h2 id={titleId} className="px-phi-1 pb-phi-n2 text-phi-1 font-bold tracking-tight text-balance">{title}</h2>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">{typeof children === "function" ? children(close) : children}</div>
          {footer ? <div className="shrink-0 px-phi-1 pt-phi-n1 pb-safe-action md:pb-phi-1">{typeof footer === "function" ? footer(close) : footer}</div> : null}
          {footer ? null : <div className="pb-safe-action md:pb-phi-1" />}
        </div>
      </div>
    </div>,
    document.body,
  );
}
